/**
 * MCP Context Sync Hook
 *
 * Synchronizes app state to the MCP server's context cache.
 * Subscribes to Zustand stores and pushes snapshots when state changes.
 * This enables AI agents to read live app state via MCP resources.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useAiLockStore } from '../../terminal/store/aiLockStore';
import { isQueryTab, isNotebookTab } from '../../workspace/core/types/workspaceTypes';
import { isCodeCell } from '../../workspace/notebook/types/notebookTypes';
import type { NotebookTabContent } from '../../workspace/notebook/types/notebookTypes';
import { createSelectionSnapshot } from '../services/selectionSnapshot';
import { engineSupports, isValidEngineId, type DatabaseEngineId } from '../../../core/engines';
import type {
  WorkspaceSnapshot,
  ConnectionsSnapshot,
  ConnectionSnapshotItem,
  ConnectionStatus,
  TabsSnapshot,
  TabSnapshotItem,
  EditorSnapshot,
  ResultsSnapshot,
  ResultSetPreview,
  SelectionSnapshot,
} from '../../../types/mcpAppContext';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

// Debounce delay for pushing updates (ms)
const SYNC_DEBOUNCE_MS = 100;

// Size limits
const MAX_SQL_LENGTH = 10000;
const MAX_PREVIEW_ROWS = 10;
const MAX_RESULT_SETS = 5;

/**
 * Creates a workspace snapshot from current state
 */
const createWorkspaceSnapshot = (
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  activeTabId: string | null
): WorkspaceSnapshot => ({
  sessionId: null, // TODO: Add session store integration
  sessionName: null,
  layoutState: 'single',
  tabCount: tabs.length,
  activeTabId,
  capturedAt: new Date().toISOString(),
});

/**
 * Creates a connections snapshot from current state.
 *
 * Exported for the capability-flag unit test (S1 Task 1.16); the hook below is
 * still its only production caller.
 */
export const createConnectionsSnapshot = (
  connections: ReturnType<typeof useConnectionStore.getState>['connections'],
  activeConnections: ReturnType<typeof useConnectionStore.getState>['activeConnections'],
  connectionOperationById: Record<string, 'connecting' | 'disconnecting' | undefined>,
  connectionErrorById: Record<string, string | undefined>,
  selectedConnectionId: string | null | undefined
): ConnectionsSnapshot => ({
  connections: connections.map((conn): ConnectionSnapshotItem => {
    const activeConn = activeConnections.find((ac) => ac.id === conn.id);
    const operation = connectionOperationById[conn.id];
    const error = connectionErrorById[conn.id];

    // Determine status with priority: connecting > error > connected/disconnected
    let status: ConnectionStatus = 'disconnected';
    if (operation === 'connecting') {
      status = 'connecting';
    } else if (error) {
      status = 'error';
    } else if (activeConn?.status === 'connected') {
      status = 'connected';
    }

    const engineId = (isValidEngineId(conn.engine ?? '') ? conn.engine : 'mssql') as DatabaseEngineId;

    return {
      id: conn.id,
      // Safe fields only (security slice): never the raw alias or server text, which can hold a secret.
      alias: safeConnectionAlias(conn),
      engine: (conn.engine as 'mssql' | 'pgsql' | 'sqlite') || 'mssql',
      server: safeConnectionServer(conn),
      database: conn.database || '',
      status,
      isActive: conn.id === selectedConnectionId,
      // Capability flags, so the agent can tell an Azure Storage connection
      // has no Query Editor / Table Explorer without reading the engine id.
      supportsQueryEditor: engineSupports(engineId, 'workspaceFeatures', 'supportsQueryEditor'),
      supportsTableExplorer: engineSupports(engineId, 'workspaceFeatures', 'supportsTableExplorer'),
    };
  }),
  selectedConnectionId: selectedConnectionId ?? null,
  capturedAt: new Date().toISOString(),
});

/**
 * Creates a tabs snapshot from current state
 */
const createTabsSnapshot = (
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  activeTabId: string | null,
  lockedTabs: Map<string, { sessionId: string; message?: string }>
): TabsSnapshot => ({
  tabs: tabs.map((tab): TabSnapshotItem => {
    const lockInfo = lockedTabs.get(tab.id);
    const isQuery = isQueryTab(tab);
    const isNotebook = isNotebookTab(tab);
    const isDirty = isQuery
      ? !!(tab.content.filePath && tab.content.query)
      : isNotebook
        ? (tab.content as NotebookTabContent).isDirty
        : false;
    return {
      id: tab.id,
      title: tab.title,
      type: tab.type,
      isActive: tab.id === activeTabId,
      isPinned: tab.isPinned ?? false,
      connectionId: tab.connectionInfo?.connectionId ?? null,
      database: tab.connectionInfo?.database ?? null,
      lockState: {
        isLocked: !!lockInfo,
        lockedBy: lockInfo ? 'ai' : null,
        lockMessage: lockInfo?.message ?? null,
      },
      isDirty,
      transactionMode: isQuery ? tab.content.transactionMode : null,
      transactionState: isQuery ? tab.content.transactionState : null,
      isolationLevel: isQuery ? tab.content.isolationLevel : null,
      cellCount: isNotebook ? (tab.content as NotebookTabContent).cells.length : null,
      filePath: isQuery ? (tab.content.filePath ?? null) : isNotebook ? ((tab.content as NotebookTabContent).filePath ?? null) : null,
    };
  }),
  activeTabId,
  capturedAt: new Date().toISOString(),
});

/**
 * Creates an editor snapshot from the active query tab
 */
const createEditorSnapshot = (
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  activeTabId: string | null
): EditorSnapshot => {
  const activeTab = activeTabId ? tabs.find((t) => t.id === activeTabId) : null;
  const emptySnapshot: EditorSnapshot = {
    tabId: null,
    tabTitle: null,
    sql: '',
    isTruncated: false,
    originalLength: 0,
    selectionRange: null,
    selectedText: null,
    hasFile: false,
    filePath: null,
    isDirty: false,
    connectionId: null,
    database: null,
    capturedAt: new Date().toISOString(),
  };

  if (!activeTab) return emptySnapshot;

  // Notebook tab: expose first code cell source as the "editor" content
  if (isNotebookTab(activeTab)) {
    const content = activeTab.content as NotebookTabContent;
    const firstCodeCell = content.cells.find(isCodeCell);
    const cellSource = firstCodeCell?.source || '';
    const isTruncated = cellSource.length > MAX_SQL_LENGTH;
    return {
      tabId: activeTab.id,
      tabTitle: activeTab.title,
      sql: isTruncated ? cellSource.slice(0, MAX_SQL_LENGTH) : cellSource,
      isTruncated,
      originalLength: cellSource.length,
      selectionRange: null,
      selectedText: null,
      hasFile: !!content.filePath,
      filePath: content.filePath ?? null,
      isDirty: content.isDirty,
      connectionId: activeTab.connectionInfo?.connectionId ?? null,
      database: activeTab.connectionInfo?.database ?? null,
      capturedAt: new Date().toISOString(),
    };
  }

  if (!isQueryTab(activeTab)) return emptySnapshot;

  const query = activeTab.content.query || '';
  const isTruncated = query.length > MAX_SQL_LENGTH;

  return {
    tabId: activeTab.id,
    tabTitle: activeTab.title,
    sql: isTruncated ? query.slice(0, MAX_SQL_LENGTH) : query,
    isTruncated,
    originalLength: query.length,
    selectionRange: null, // TODO: Add editor selection tracking
    selectedText: null,
    hasFile: !!activeTab.content.filePath,
    filePath: activeTab.content.filePath ?? null,
    isDirty: !!(activeTab.content.filePath && activeTab.content.query),
    connectionId: activeTab.connectionInfo?.connectionId ?? null,
    database: activeTab.connectionInfo?.database ?? null,
    capturedAt: new Date().toISOString(),
  };
};

/**
 * Creates a results snapshot from the active query tab
 */
const createResultsSnapshot = (
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  activeTabId: string | null
): ResultsSnapshot => {
  const activeTab = activeTabId ? tabs.find((t) => t.id === activeTabId) : null;

  if (!activeTab || !isQueryTab(activeTab)) {
    return {
      tabId: null,
      tabTitle: null,
      resultSets: [],
      resultSetCount: 0,
      isExecuting: false,
      executionTimeMs: null,
      error: null,
      connectionId: null,
      database: null,
      capturedAt: new Date().toISOString(),
    };
  }

  const resultSets = activeTab.content.resultSets || [];
  const messages = activeTab.content.messages || [];
  const limitedResultSets = resultSets.slice(0, MAX_RESULT_SETS);

  // Detect execution state from messages
  // If the last message is "Executing query..." or "Cancelling query...", we're executing
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isExecuting =
    lastMessage?.content === 'Executing query...' ||
    lastMessage?.content === 'Cancelling query...';

  // Detect error from messages - find the last error message
  const errorMessages = messages.filter((m) => m.type === 'error');
  const lastError = errorMessages.length > 0 ? errorMessages[errorMessages.length - 1]?.content : null;

  const resultSetPreviews: ResultSetPreview[] = limitedResultSets.map((rs, index) => {
    const rows = Array.isArray(rs.rows) ? rs.rows : [];
    const previewRows = rows.slice(0, MAX_PREVIEW_ROWS);
    return {
      index,
      columns: (rs.columns ?? []).map((col) => ({
        name: col.name,
        dataType: col.dataType || 'unknown',
      })),
      columnCount: (rs.columns ?? []).length,
      rowCount: rows.length,
      previewRows,
      previewRowCount: previewRows.length,
      isTruncated: rows.length > MAX_PREVIEW_ROWS,
    };
  });

  return {
    tabId: activeTab.id,
    tabTitle: activeTab.title,
    resultSets: resultSetPreviews,
    resultSetCount: resultSets.length,
    isExecuting,
    executionTimeMs: activeTab.content.stats?.executionTimeMs ?? null,
    error: lastError,
    connectionId: activeTab.connectionInfo?.connectionId ?? null,
    database: activeTab.connectionInfo?.database ?? null,
    capturedAt: new Date().toISOString(),
  };
};

/**
 * Hook to sync app state to MCP context cache.
 * Should be used in a top-level component (e.g., App or MainShell).
 */
export function useMcpContextSync() {
  // Debounce timers
  const workspaceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const connectionsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tabsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const editorTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resultsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const selectionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced push functions
  const pushWorkspaceSnapshot = useCallback((snapshot: WorkspaceSnapshot) => {
    if (workspaceTimerRef.current) clearTimeout(workspaceTimerRef.current);
    workspaceTimerRef.current = setTimeout(() => {
      window.electronAPI?.mcpAppContext?.updateWorkspace(snapshot);
    }, SYNC_DEBOUNCE_MS);
  }, []);

  const pushConnectionsSnapshot = useCallback((snapshot: ConnectionsSnapshot) => {
    if (connectionsTimerRef.current) clearTimeout(connectionsTimerRef.current);
    connectionsTimerRef.current = setTimeout(() => {
      window.electronAPI?.mcpAppContext?.updateConnections(snapshot);
    }, SYNC_DEBOUNCE_MS);
  }, []);

  const pushTabsSnapshot = useCallback((snapshot: TabsSnapshot) => {
    if (tabsTimerRef.current) clearTimeout(tabsTimerRef.current);
    tabsTimerRef.current = setTimeout(() => {
      window.electronAPI?.mcpAppContext?.updateTabs(snapshot);
    }, SYNC_DEBOUNCE_MS);
  }, []);

  const pushEditorSnapshot = useCallback((snapshot: EditorSnapshot) => {
    if (editorTimerRef.current) clearTimeout(editorTimerRef.current);
    editorTimerRef.current = setTimeout(() => {
      window.electronAPI?.mcpAppContext?.updateEditor(snapshot);
    }, SYNC_DEBOUNCE_MS);
  }, []);

  const pushResultsSnapshot = useCallback((snapshot: ResultsSnapshot) => {
    if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
    resultsTimerRef.current = setTimeout(() => {
      window.electronAPI?.mcpAppContext?.updateResults(snapshot);
    }, SYNC_DEBOUNCE_MS);
  }, []);

  const pushSelectionSnapshot = useCallback((snapshot: SelectionSnapshot) => {
    if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
    selectionTimerRef.current = setTimeout(() => {
      window.electronAPI?.mcpAppContext?.updateSelection(snapshot);
    }, SYNC_DEBOUNCE_MS);
  }, []);

  // Subscribe to workspace store changes
  useEffect(() => {
    const unsubscribe = useWorkspaceStore.subscribe((state, prevState) => {
      const tabsChanged = state.tabs !== prevState.tabs;
      const activeTabChanged = state.activeTabId !== prevState.activeTabId;

      if (tabsChanged || activeTabChanged) {
        const lockedTabs = useAiLockStore.getState().lockedTabs;

        // Update workspace snapshot
        pushWorkspaceSnapshot(createWorkspaceSnapshot(state.tabs, state.activeTabId));

        // Update tabs snapshot (with lock state)
        pushTabsSnapshot(createTabsSnapshot(state.tabs, state.activeTabId, lockedTabs));

        // Update editor snapshot
        pushEditorSnapshot(createEditorSnapshot(state.tabs, state.activeTabId));

        // Update results snapshot
        pushResultsSnapshot(createResultsSnapshot(state.tabs, state.activeTabId));
      }
    });

    // Initial sync
    const state = useWorkspaceStore.getState();
    const lockedTabs = useAiLockStore.getState().lockedTabs;
    pushWorkspaceSnapshot(createWorkspaceSnapshot(state.tabs, state.activeTabId));
    pushTabsSnapshot(createTabsSnapshot(state.tabs, state.activeTabId, lockedTabs));
    pushEditorSnapshot(createEditorSnapshot(state.tabs, state.activeTabId));
    pushResultsSnapshot(createResultsSnapshot(state.tabs, state.activeTabId));

    return () => {
      unsubscribe();
      if (workspaceTimerRef.current) clearTimeout(workspaceTimerRef.current);
      if (tabsTimerRef.current) clearTimeout(tabsTimerRef.current);
      if (editorTimerRef.current) clearTimeout(editorTimerRef.current);
      if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
    };
  }, [pushWorkspaceSnapshot, pushTabsSnapshot, pushEditorSnapshot, pushResultsSnapshot]);

  // Subscribe to connection store changes
  useEffect(() => {
    const unsubscribe = useConnectionStore.subscribe((state, prevState) => {
      const connectionsChanged = state.connections !== prevState.connections;
      const activeChanged = state.activeConnections !== prevState.activeConnections;
      const operationChanged = state.connectionOperationById !== prevState.connectionOperationById;
      const errorChanged = state.connectionErrorById !== prevState.connectionErrorById;

      if (connectionsChanged || activeChanged || operationChanged || errorChanged) {
        // Get selected connection from context selection store
        const selectedConnectionId = useDatabaseUIStore.getState().selectedConnectionId;
        pushConnectionsSnapshot(
          createConnectionsSnapshot(
            state.connections,
            state.activeConnections,
            state.connectionOperationById,
            state.connectionErrorById,
            selectedConnectionId
          )
        );
      }
    });

    // Initial sync
    const state = useConnectionStore.getState();
    const selectedConnectionId = useDatabaseUIStore.getState().selectedConnectionId;
    pushConnectionsSnapshot(
      createConnectionsSnapshot(
        state.connections,
        state.activeConnections,
        state.connectionOperationById,
        state.connectionErrorById,
        selectedConnectionId
      )
    );

    return () => {
      unsubscribe();
      if (connectionsTimerRef.current) clearTimeout(connectionsTimerRef.current);
    };
  }, [pushConnectionsSnapshot]);

  // Subscribe to context selection store changes
  useEffect(() => {
    const unsubscribe = useDatabaseUIStore.subscribe((state, prevState) => {
      const connectionChanged = state.selectedConnectionId !== prevState.selectedConnectionId;
      const nodeChanged = state.selectedNodeId !== prevState.selectedNodeId;
      const databaseChanged = state.selectedDatabase !== prevState.selectedDatabase;

      if (connectionChanged) {
        // Update connections snapshot with new selection
        const connState = useConnectionStore.getState();
        pushConnectionsSnapshot(
          createConnectionsSnapshot(
            connState.connections,
            connState.activeConnections,
            connState.connectionOperationById,
            connState.connectionErrorById,
            state.selectedConnectionId
          )
        );
      }

      // Update selection snapshot when node selection changes
      if (nodeChanged || connectionChanged || databaseChanged) {
        pushSelectionSnapshot(
          createSelectionSnapshot(
            state.selectedNodeId,
            state.selectedConnectionId,
            state.selectedDatabase
          )
        );
      }
    });

    // Initial selection sync
    const state = useDatabaseUIStore.getState();
    pushSelectionSnapshot(
      createSelectionSnapshot(
        state.selectedNodeId,
        state.selectedConnectionId,
        state.selectedDatabase
      )
    );

    return () => {
      unsubscribe();
      if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
    };
  }, [pushConnectionsSnapshot, pushSelectionSnapshot]);

  // Subscribe to AI lock store changes
  useEffect(() => {
    const unsubscribe = useAiLockStore.subscribe((state, prevState) => {
      // If any locks changed, update tabs snapshot
      if (state.lockedTabs !== prevState.lockedTabs) {
        const workspaceState = useWorkspaceStore.getState();
        pushTabsSnapshot(
          createTabsSnapshot(workspaceState.tabs, workspaceState.activeTabId, state.lockedTabs)
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [pushTabsSnapshot]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      [
        workspaceTimerRef,
        connectionsTimerRef,
        tabsTimerRef,
        editorTimerRef,
        resultsTimerRef,
        selectionTimerRef,
      ].forEach((ref) => {
        if (ref.current) clearTimeout(ref.current);
      });
    };
  }, []);
}
